import React, { useMemo, useState } from "react";
import { Pressable, ScrollView, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";

import { getAIExhibitCopy } from "../../content/aiExhibitContent";
import { getAIExhibitScreen } from "../../aiExhibitScreenRegistry";

const SECTIONS = [
  { id: "what_is_ai", icon: "🧠", title: "What is AI?", subtitle: "Definitions, boundaries, and the AI family" },
  { id: "history", icon: "⏳", title: "A short history", subtitle: "From Turing to today's generative systems" },
  { id: "how_it_works", icon: "⚙️", title: "How it works", subtitle: "Training, neural networks, tokens, and assistants" },
  { id: "impact", icon: "🌎", title: "AI & our world", subtitle: "People, society, work, and the planet" },
  { id: "sources", icon: "📎", title: "Sources", subtitle: "Where the exhibit's claims and figures come from" },
];

export default function LibraryBridgeScreen({
  language = "en",
  onBack = () => {},
  onOpenAsk = () => {},
  visitorUserId,
  clientSessionId,
}) {
  const [readingId, setReadingId] = useState(null);
  const copy = useMemo(() => getAIExhibitCopy(language), [language]);

  const navigateReading = (nextId) => {
    if (!nextId || nextId === "home" || nextId === "attract") {
      setReadingId(null);
      return;
    }

    if (nextId === "ask_ai") {
      onOpenAsk();
      return;
    }

    setReadingId(nextId);
  };

  if (!readingId) {
    return (
      <View style={s.container}>
        <View style={s.top}>
          <Pressable onPress={onBack}><Text style={s.topText}>← Menu</Text></Pressable>
          <Text style={s.topText}>Dig deeper</Text>
        </View>

        <View style={s.menu}>
          <Text style={s.title}>Dig deeper.</Text>
          <Text style={s.subtitle}>
            These are the deeper reading sections already built in Saras AI Explained.
            The new games sit on top; the existing educational material stays available underneath.
          </Text>

          <View style={s.grid}>
            {SECTIONS.map((section) => (
              <Pressable
                key={section.id}
                onPress={() => setReadingId(section.id)}
                style={({ pressed }) => [s.card, pressed && s.cardPressed]}
              >
                <Text style={s.icon}>{section.icon}</Text>
                <Text style={s.cardTitle}>{section.title}</Text>
                <Text style={s.cardSub}>{section.subtitle}</Text>
              </Pressable>
            ))}
          </View>

          <Pressable onPress={onBack} style={s.primary}>
            <Text style={s.primaryText}>Back to the games →</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const config = getAIExhibitScreen(readingId);
  const ReadingComponent = config.component;
  const currentTitle = SECTIONS.find((section) => section.id === readingId)?.title || "Reading library";

  return (
    <View style={s.container}>
      <View style={s.top}>
        <Pressable onPress={() => setReadingId(null)}><Text style={s.topText}>← Reading library</Text></Pressable>
        <Text style={s.topText}>{currentTitle}</Text>
        <Pressable onPress={onBack} style={s.gamesLink}><Text style={s.topText}>⌂ Games</Text></Pressable>
      </View>

      <ScrollView
        style={s.scroll}
        contentContainerStyle={s.scrollContent}
        showsVerticalScrollIndicator
      >
        <ReadingComponent
          navigate={navigateReading}
          language={language}
          copy={copy}
          audioEnabled={false}
          visitorUserId={visitorUserId}
          clientSessionId={clientSessionId}
          askConversationStarted={false}
          onAskConversationStarted={() => {}}
        />
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  top: { height: 72, paddingHorizontal: 48, flexDirection: "row", gap: 26, alignItems: "center", borderBottomWidth: 1, borderBottomColor: "#132037" },
  topText: { color: "#8FA5BC", fontSize: 19, fontWeight: "800" },
  gamesLink: { marginLeft: "auto", marginRight: 160 },
  menu: { alignItems: "center", paddingHorizontal: 100 },
  title: { marginTop: 88, color: "#F4F8FC", fontSize: 68, fontWeight: "900" },
  subtitle: { marginTop: 18, color: "#9FB2C6", fontSize: 25, lineHeight: 38, maxWidth: 1250, textAlign: "center" },
  grid: { marginTop: 46, width: 1550, flexDirection: "row", flexWrap: "wrap", justifyContent: "center", gap: 20 },
  card: { width: 490, minHeight: 210, padding: 28, borderRadius: 22, borderWidth: 1, borderColor: "#1B2A44", backgroundColor: "#0D1626" },
  cardPressed: { borderColor: "#4FD1FF", backgroundColor: "#0E2237" },
  icon: { fontSize: 42 },
  cardTitle: { marginTop: 12, color: "#F4F8FC", fontSize: 28, fontWeight: "900" },
  cardSub: { marginTop: 8, color: "#9FB2C6", fontSize: 18, lineHeight: 27 },
  primary: { marginTop: 42, borderWidth: 1, borderColor: "#4FD1FF", backgroundColor: "#0E3A50", borderRadius: 14, paddingHorizontal: 28, paddingVertical: 16 },
  primaryText: { color: "#F4F8FC", fontSize: 20, fontWeight: "900" },
  scroll: { height: 1008 },
  scrollContent: { paddingBottom: 100 },
});
